/**
 * D1 Origen — Scoring de calidad de la fuente de datos
 *
 * Evalúa la fiabilidad intrínseca de la fuente que proporcionó
 * las coordenadas, considerando:
 *   - Nivel de cascada (L0 más fiable → L5 menos fiable)
 *   - Tipo de fuente (especializada > genérica)
 *   - Modo offline (R2/cache) vs API en vivo
 *
 * @module services/scoring/d1Origen
 * @version 1.0.0
 */

import type { GeocodingLevel, GeocodingSource } from '../../types/errors/GeocodingErrors';
import type { D1OrigenInput, D1OrigenResult } from '../../types/scoring';

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/**
 * Score base por nivel de cascada.
 * Refleja la confianza inherente de cada nivel.
 */
const BASE_SCORE_BY_LEVEL: Record<GeocodingLevel, number> = {
  L0: 95,  // Cache/Singleton — dato ya verificado
  L1: 85,  // WFS Especializado — fuente autoritativa
  L2: 70,  // CartoCiudad IGN — buena calidad
  L3: 60,  // CDAU Andalucía — calidad variable
  L4: 50,  // IDEE Nacional — cobertura amplia, menos precisa
  L5: 40,  // Nominatim OSM — voluntario, calidad variable
};

/**
 * Bonus para fuentes especializadas WFS y R2 (L1).
 * Las fuentes autoritativas reciben bonus adicional sobre el base.
 * Las fuentes R2 son datos idénticos al WFS pero precargados en Cloudflare R2.
 */
const SPECIALIZED_BONUS: Partial<Record<GeocodingSource, number>> = {
  // ── WFS en vivo ──
  wfs_sicess:    10,  // SAS/SICESS — directorio oficial sanitario
  wfs_education:  8,  // Educación — directorio oficial
  wfs_iaph:       7,  // IAPH — patrimonio histórico
  wfs_security:   6,  // Seguridad — registro oficial
  wfs_rsu:        5,  // RSU — DERA G14
  rest_miteco:    8,  // MITECO — gasolineras oficial
  ports_andalucia: 6, // Puertos DERA
  ports_andalucia_validated: 9, // Puertos validados cruzadamente
  // ── R2 especializados (misma calidad que WFS, offline) ──
  dera_r2:         7,  // DERA G12 servicios (salud/edu/seg)
  dera_g10_r2:     6,  // DERA G10 energía/medioambiente
  dera_g3_r2:      5,  // DERA G3 depósitos agua
  dera_viaria_r2:  4,  // DERA viaria centroides
  nga_r2:          6,  // NGA topónimos IECA
  iaph_r2:         7,  // IAPH patrimonio cultural
  cdau_r2:         3,  // CDAU callejero (L3, menor bonus)
  // Catastro DGC: coordenadas de centroide de parcela verificadas legalmente.
  // Precisión ~±5 m (superior a CartoCiudad ±12 m). Bonus acorde a autoridad.
  catastro_r2:     8,  // Catastro R2 preloaded — DGC autoritativo (antes: 2)
  catastro_api:    6,  // Catastro API en vivo — DGC autoritativo (antes: ausente → 0)
};

/**
 * Bonus por usar fuente offline (R2/cache).
 * Los datos precargados en R2 son deterministas y no dependen
 * de disponibilidad de API.
 */
const OFFLINE_BONUS = 3;

/**
 * Penalización para fuentes de último recurso
 */
const FALLBACK_PENALTIES: Partial<Record<GeocodingSource, number>> = {
  centroid: -30,  // Centroide municipal: muy impreciso
  manual:     0,  // Manual: el usuario validó
};

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Calcula el componente D1 Origen del scoring v2.
 *
 * Algoritmo:
 * 1. Score base según nivel de cascada
 * 2. + Bonus por fuente especializada (WFS autoritativo)
 * 3. + Bonus por modo offline (R2/cache, determinista)
 * 4. + Penalización por fuente fallback (centroide)
 * 5. Clamp [0, 100]
 *
 * @example
 * ```ts
 * const d1 = computeD1Origen({
 *   cascadeLevel: 'L1',
 *   source: 'wfs_sicess',
 *   offline: false,
 *   geocoderUsed: 'specialized:health',
 * });
 * // d1.score → 95 (85 base + 10 bonus SAS)
 * ```
 */
export function computeD1Origen(input: D1OrigenInput): D1OrigenResult {
  const { cascadeLevel, source, offline } = input;

  // 1. Score base por nivel
  const baseScore = BASE_SCORE_BY_LEVEL[cascadeLevel] ?? 40;

  // 2. Bonus por fuente especializada
  const specializedBonus = SPECIALIZED_BONUS[source] ?? 0;

  // 3. Bonus offline
  const offlineBonus = offline ? OFFLINE_BONUS : 0;

  // 4. Penalización fallback
  const fallbackPenalty = FALLBACK_PENALTIES[source] ?? 0;

  // 5. Score total, clamped [0, 100]
  const rawScore = baseScore + specializedBonus + offlineBonus + fallbackPenalty;
  const score = Math.max(0, Math.min(100, rawScore));

  // Desglose legible
  const parts: string[] = [`base=${baseScore} (${cascadeLevel})`];
  if (specializedBonus > 0) parts.push(`+${specializedBonus} (${source})`);
  if (offlineBonus > 0) parts.push(`+${offlineBonus} (offline)`);
  if (fallbackPenalty !== 0) parts.push(`${fallbackPenalty} (fallback)`);

  return {
    score,
    cascadeLevel,
    source,
    decayApplied: 95 - baseScore, // Decay relativo a L0 (95)
    offlineBonus,
    detail: `D1=${score}: ${parts.join(', ')}`,
  };
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Infiere el nivel de cascada a partir del string geocoderUsed
 * del OrchestrationResult existente.
 *
 * Mapeo:
 * - 'cache', 'singleton' → L0
 * - 'specialized:*', 'wfs_*', 'rest_*', 'ports_*' → L1
 * - 'cartociudad', 'catastro*' → L2  (Catastro DGC es fuente autoritativa, fix 2026-03-13)
 * - 'cdau' → L3
 * - 'idee', '*_r2' (excepto catastro) → L4
 * - 'nominatim' → L5
 */
export function inferCascadeLevel(geocoderUsed: string): GeocodingLevel {
  const g = geocoderUsed.toLowerCase();

  if (g === 'cache' || g === 'singleton' || g === 'none') return 'L0';
  if (g.startsWith('specialized:') || g.startsWith('wfs_') ||
      g.startsWith('rest_') || g.startsWith('ports_')) return 'L1';
  if (g === 'cartociudad' || g.includes('cartociudad')) return 'L2';
  // Catastro DGC es fuente autoritativa (registro legal con coordenadas verificadas).
  // Se equipara a CartoCiudad IGN en L2, NO a IDEE genérico en L4.
  // Incluye catastro_r2 y catastro_api.
  if (g.startsWith('catastro')) return 'L2';
  if (g === 'cdau' && !g.includes('r2')) return 'L3';
  if (g === 'idee' || g.includes('_r2')) return 'L4';
  if (g === 'nominatim' || g.includes('nominatim')) return 'L5';
  // BUG-11 FIX: 'centroide' (español) y 'centroid' → L5 (último recurso)
  if (g === 'centroide' || g === 'centroid' || g.includes('centroid') || g.includes('centroide')) return 'L5';

  // Fallback conservador
  return 'L5';
}

/**
 * Infiere la GeocodingSource a partir del string geocoderUsed.
 * Normaliza strings como 'specialized:health' → 'wfs_sicess'.
 */
export function inferSource(geocoderUsed: string): GeocodingSource {
  const g = geocoderUsed.toLowerCase();

  // Mapeo de specialized:* a fuentes WFS
  const specializedMap: Record<string, GeocodingSource> = {
    'specialized:health':    'wfs_sicess',
    'specialized:education': 'wfs_education',
    'specialized:cultural':  'wfs_iaph',
    'specialized:security':  'wfs_security',
    'specialized:emergency': 'wfs_security',
    'specialized:fire':      'wfs_security',
    'specialized:police':    'wfs_security',
  };

  if (specializedMap[g]) return specializedMap[g];

  // Fuentes directas
  const directSources: GeocodingSource[] = [
    'cache', 'wfs_dera', 'wfs_sicess', 'wfs_education', 'wfs_iaph',
    'wfs_security', 'wfs_rsu', 'rest_miteco',
    'ports_andalucia_validated', 'ports_nacional_ign', 'ports_nacional_estado',
    'ports_andalucia',
    'nomenclator', 'cartociudad',
    'cdau_r2', 'cdau',
    'catastro_r2', 'catastro_api',
    'dera_r2', 'nga_r2', 'ine_r2',
    'idee', 'nominatim',
    'centroid', 'manual',
  ];

  // Exact match primero (evita falsos positivos con includes)
  for (const s of directSources) {
    if (g === s) return s;
  }
  // BUG-11 FIX: el orquestador usa 'centroide' (español) para el fallback municipal.
  // Sin esta normalización, inferSource devolvía 'nominatim' en lugar de 'centroid',
  // haciendo que FALLBACK_PENALTIES[-30] no se aplicara → score 88-90 en lugar de ~22-32.
  if (g === 'centroide' || g.includes('centroide')) return 'centroid';
  for (const s of directSources) {
    if (g.includes(s)) return s;
  }

  // Fallback
  return 'nominatim';
}

/**
 * Crea D1OrigenInput a partir de los datos del OrchestrationResult existente.
 * Función puente para facilitar la migración scoring v1 → v2.
 */
export function buildD1Input(
  geocoderUsed: string,
  sourceHint?: string,
  isOffline?: boolean,
): D1OrigenInput {
  const source = sourceHint
    ? inferSource(sourceHint)
    : inferSource(geocoderUsed);

  const cascadeLevel = inferCascadeLevel(geocoderUsed);

  // Inferir offline: R2 sources y cache son offline
  const offline = isOffline ?? (
    source === 'cache' ||
    source.endsWith('_r2') ||
    geocoderUsed.toLowerCase() === 'singleton'
  );

  return {
    cascadeLevel,
    source,
    offline,
    geocoderUsed,
  };
}
